import { useMemo } from "react";
import { useSelector } from "react-redux";

import { MODULE_STATE, getModuleState } from "@/permissions/modules";
import { TABS } from "../constants/tabs";

const NO_STORED_TABS = [];

/**
 * The candidate-analysis tabs this user may see, for this candidate.
 *
 * Three rules, in order:
 *
 * 1. A tab that names no module always shows.
 * 2. A tab whose module the tenant **is** entitled to always shows. This
 *    includes a module that is dark fleet-wide: the kill switch stops new
 *    work, and the panel inside disables only the action, so everything
 *    generated before the kill stays on screen.
 * 3. A tab whose module the tenant is **not** entitled to shows only when this
 *    candidate already has something stored behind it — and the panel then
 *    offers no way to make more.
 *
 * Rule 3 is a deliberate exception to "not entitled means invisible". The
 * general rule protects a tenant from being shown a feature they never bought;
 * it was never meant to delete work they did buy. Withdrawing
 * `ai.candidate_job_fit` used to take away an analysis the tenant had already
 * paid for and could still read a minute earlier. A candidate with nothing
 * stored still gets no tab, so nobody is shown a feature they never had.
 *
 * The server enforces the same split: the read routes carry no module gate,
 * and `.../job-fit/analyze` and `.../interview-questions/generate/{id}` do.
 *
 * @param {string[]} storedTabIds ids of tabs with stored data for this
 *   candidate. **Memoize it** — it is a dependency of the result.
 */
export const useVisibleTabs = (storedTabIds = NO_STORED_TABS) => {
  const user = useSelector((state) => state.auth?.user);

  return useMemo(() => {
    const stored = new Set(storedTabIds);
    return TABS.filter((tab) => {
      if (!tab.module) return true;
      if (getModuleState(tab.module, user) !== MODULE_STATE.NOT_ENTITLED) return true;
      return stored.has(tab.id);
    });
  }, [user, storedTabIds]);
};

export default useVisibleTabs;
